<template>
  <BottomSheet ref="bottomSheet" :overlayerCanDismiss="step !== 1" hashBase="payment">
    <div class="relative w-full p-6">
      
      <!-- Close button -->
      <button @click="closeModal" class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full transition-colors z-10">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
      </button>

      <div class="text-2xl font-bold title-color mb-5 pr-8">
        Paiement de l'achat
      </div>

      <div class="mb-5 p-4 bg-primary/10 rounded-2xl border border-primary/20 flex flex-col items-center">
        <div class="text-sm font-bold text-gray-600 mb-1 flex items-center justify-center gap-2">
          <span class="truncate max-w-[200px]">{{ product?.name }}</span>
          <span class="bg-primary/20 text-primary px-2 py-0.5 rounded-full">x{{ quantity || 1 }}</span>
        </div>
        <div class="text-3xl font-black text-primary">
          {{ formatPrice((product?.price || 0) * (quantity || 1)) }}
        </div>
      </div>

      <!-- Step 0: Select payment method -->
      <div v-if="step === 0" class="flex flex-col gap-4">
        <div>
          <label class="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Mode de paiement</label>
          <div class="grid grid-cols-2 gap-3">
            <div 
              class="relative flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all"
              :class="paymentMethod === 'wallet' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'"
              @click="paymentMethod = 'wallet'"
            >
              <div class="text-center">
                <div class="mx-auto w-8 h-8 rounded-full mb-2 flex items-center justify-center" :class="paymentMethod === 'wallet' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
                </div>
                <div class="font-bold text-sm" :class="paymentMethod === 'wallet' ? 'text-primary' : 'text-gray-600'">Wallet</div>
              </div>
            </div>
            
            <div 
              class="relative flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all"
              :class="paymentMethod === 'mobile_money' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'"
              @click="paymentMethod = 'mobile_money'"
            >
              <div class="text-center">
                <div class="mx-auto w-8 h-8 rounded-full mb-2 flex items-center justify-center" :class="paymentMethod === 'mobile_money' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                </div>
                <div class="font-bold text-sm" :class="paymentMethod === 'mobile_money' ? 'text-primary' : 'text-gray-600'">Mobile Money</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="paymentMethod === 'mobile_money'" class="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-4">
          <div>
            <label class="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Opérateur</label>
            <div class="flex gap-3">
              <label class="flex-1 flex items-center justify-center gap-2 p-3 border rounded-xl cursor-pointer bg-white transition-all" :class="provider === 'mtn' ? 'border-primary ring-1 ring-primary' : 'border-gray-200'">
                <input type="radio" v-model="provider" value="mtn" class="hidden">
                <div class="w-4 h-4 rounded-full border flex items-center justify-center" :class="provider === 'mtn' ? 'border-primary' : 'border-gray-300'">
                  <div v-if="provider === 'mtn'" class="w-2 h-2 rounded-full bg-primary"></div>
                </div>
                <span class="font-bold text-sm">MTN</span>
              </label>
              <label class="flex-1 flex items-center justify-center gap-2 p-3 border rounded-xl cursor-pointer bg-white transition-all" :class="provider === 'moov' ? 'border-primary ring-1 ring-primary' : 'border-gray-200'">
                <input type="radio" v-model="provider" value="moov" class="hidden">
                <div class="w-4 h-4 rounded-full border flex items-center justify-center" :class="provider === 'moov' ? 'border-primary' : 'border-gray-300'">
                  <div v-if="provider === 'moov'" class="w-2 h-2 rounded-full bg-primary"></div>
                </div>
                <span class="font-bold text-sm">Moov</span>
              </label>
            </div>
          </div>
          <div>
            <label class="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Numéro de téléphone</label>
            <input
              v-model="phoneNumber"
              type="tel"
              placeholder="Ex: 01 22 33 44 55"
              class="w-full p-4 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all font-bold tracking-wide"
              required
            />
          </div>
        </div>
      </div>

      <!-- Step 1: Loading -->
      <div v-else-if="step === 1" class="flex flex-col items-center justify-center py-10">
        <div class="w-16 h-16 relative flex items-center justify-center">
          <div class="absolute inset-0 rounded-full border-4 border-gray-100"></div>
          <div class="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
          <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
        </div>
        <div class="text-lg font-bold title-color mt-6 text-center">
          <span v-if="paymentMethod === 'wallet'">Traitement via portefeuille...</span>
          <span v-else-if="paymentMethod === 'mobile_money'">Initialisation Mobile Money...</span>
        </div>
        <div class="text-sm text-gray-500 mt-2 text-center">Veuillez patienter quelques instants.</div>
      </div>

      <!-- Step 2: Result -->
      <div v-else-if="step === 2" class="flex flex-col gap-5 py-4">
        <div class="text-center">
          <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path></svg>
          </div>
          <div class="text-2xl font-black text-gray-900 mb-2">
            <span v-if="paymentMethod === 'wallet'">Paiement réussi !</span>
            <span v-else-if="paymentMethod === 'mobile_money'">Requête envoyée</span>
          </div>
          <p class="text-gray-500" v-if="paymentMethod === 'mobile_money'">Veuillez valider le paiement sur votre téléphone via le prompt USSD.</p>
        </div>

        <div v-if="paymentResponse" class="bg-gray-50 rounded-2xl p-4 border border-gray-100 text-sm">
          <div v-if="paymentResponse.status" class="flex justify-between items-center mb-3">
            <span class="text-gray-500 font-medium">Statut</span>
            <span class="font-bold px-3 py-1 bg-green-100 text-green-700 rounded-full">{{ getStatusLabel(paymentResponse.status) }}</span>
          </div>
          <div v-if="paymentResponse.transactionId" class="flex justify-between items-center mb-3">
            <span class="text-gray-500 font-medium">Référence</span>
            <span class="font-bold font-mono">{{ paymentResponse.transactionId }}</span>
          </div>
          <div v-if="paymentResponse.balanceAfter !== undefined" class="flex justify-between items-center pt-3 border-t border-gray-200">
            <span class="text-gray-500 font-medium">Nouveau solde</span>
            <span class="font-black text-primary">{{ formatPrice(paymentResponse.balanceAfter) }}</span>
          </div>
        </div>

        <div v-if="paymentMethod === 'mobile_money' && pollingActive" class="flex items-center justify-center gap-3 p-4 bg-orange-50 rounded-xl text-orange-600 font-bold text-sm">
          <svg class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
          En attente de votre validation mobile...
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3 mt-6" v-if="step === 0">
        <button
          @click="initiatePayment"
          class="flex-1 py-4 px-6 rounded-2xl font-black text-white shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
          :class="isFormValid() ? 'bg-primary shadow-primary/30 hover:shadow-xl hover:shadow-primary/40' : 'bg-gray-400 shadow-none'"
          :disabled="!isFormValid()"
        >
          Payer {{ formatPrice((product?.price || 0) * (quantity || 1)) }}
        </button>
      </div>

      <div class="flex gap-3 mt-6" v-if="step === 2">
        <button
          @click="closeModal"
          class="flex-1 py-4 px-6 rounded-2xl font-black border-2 border-primary text-primary hover:bg-primary/5 transition-all"
        >
          {{ paymentMethod === 'mobile_money' && pollingActive ? 'Fermer (vérification en arrière-plan)' : 'Excellent !' }}
        </button>
      </div>
    </div>
    
    <!-- Action component for trigger -->
    <ActionTrigger
      ref="paymentTrigger"
      dataKey="payProductPurchase"
      @success="onPaymentConfirmed"
      @failed="onPaymentInitiationFailed"
    />
  </BottomSheet>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import BottomSheet from '@/presentation/components/dialog/BottomSheet.vue'

const paymentTrigger = ref(null)
const bottomSheet = ref(null)
const step = ref(0)
const product = ref(null)
const quantity = ref(1)
const purchaseId = ref(null)
const paymentMethod = ref('wallet')
const provider = ref('mtn')
const phoneNumber = ref('')
const paymentResponse = ref(null)
const pollingActive = ref(false)
let pollingInterval = null

let openPaymentModalHandler = null

onMounted(() => {
  console.log('✅ ProductPurchasePaymentModal mounted')
  openPaymentModalHandler = Events.handle('show_product_purchase_payment_modal', (data) => {
    console.log('📩 show_product_purchase_payment_modal received', data)
    openPaymentModal(data)
  })
})

onUnmounted(() => {
  if (openPaymentModalHandler) {
    Events.removeHandler(openPaymentModalHandler)
  }
})

function formatPrice(price) {
  if (!price) return '0 FCFA'
  return new Intl.NumberFormat('fr-BJ', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0
  }).format(price)
}

function openPaymentModal(data) {
  console.log('💳 Opening payment modal with data:', data)
  product.value = data.product
  quantity.value = data.quantity || 1
  purchaseId.value = data.purchaseId
  phoneNumber.value = data.phone || ''
  
  step.value = 0
  paymentMethod.value = 'wallet'
  provider.value = 'mtn'
  paymentResponse.value = null
  
  stopPolling()
  bottomSheet.value?.show()
}

function closeModal() {
  bottomSheet.value?.hide()
  stopPolling()
}

function isFormValid() {
  if (paymentMethod.value === 'wallet') return true
  if (paymentMethod.value === 'mobile_money') {
    return provider.value && phoneNumber.value && phoneNumber.value.length >= 8
  }
  return false
}

function initiatePayment() {
  if (!isFormValid()) return
  
  step.value = 1
  
  const payload = {
    purchaseId: purchaseId.value,
    paymentMethod: paymentMethod.value,
    ...(paymentMethod.value === 'mobile_money' && {
      provider: provider.value,
      phoneNumber: phoneNumber.value.replace(/\s/g, ''),
    }),
  }
  
  // Appeler l'action API
  if (paymentTrigger.value) {
    paymentTrigger.value.trigger(payload)
  }
}

function onPaymentConfirmed(res) {
  paymentResponse.value = res?.result?.data || {}
  
  if (paymentMethod.value === 'wallet') {
    step.value = 2
    Events.dispatch('toast_notification_requested', {
      type: 'success',
      title: 'Paiement réussi',
      body: 'Le montant a été débité de votre portefeuille.',
    })
    Events.dispatch('product_purchase_payment_confirmed', paymentResponse.value)
  } else if (paymentMethod.value === 'mobile_money') {
    step.value = 2
    startPolling()
  }
}

function onPaymentInitiationFailed(err) {
  step.value = 0
  
  // Chercher le message d'erreur opérationnel et affichable dans la structure imbriquée
  // On priorise le flag isDisplayable car certains messages opérationnels peuvent être techniques (ex: FedaPay)
  const rawError = err?.result?.data?.raw?.error || err?.result?.raw?.error || err?.error?.metadata?.res?.raw?.error
  const isDisplayable = err?.result?.isDisplayable || rawError?.isDisplayable || (rawError?.isOperational && rawError?.isDisplayable)
  
  const message = (isDisplayable && (err?.result?.message || rawError?.message)) 
    || 'Erreur lors de l\'initialisation du paiement.'

  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    type: 'error',
    title: 'Paiement échoué',
    body: message,
  })
}

// Fonction asymulée pour la vérification du paiement MM car on a pas le endpoint de polling implémenté dans le fetcher standard
// L'idéal serait d'écouter les webhooks, mais en fallback local:
function startPolling() {
  if (pollingActive.value) return
  pollingActive.value = true
  
  // Simuler le statut puisque le websockets ou le fetcher specifique status n'est pas dans fetchDataConfigs pour l'instant
  // Mais l'event peut notifier la page parente d'actualiser le tableau général de toute facon.
  pollingInterval = setTimeout(() => {
    Events.dispatch('product_purchase_payment_confirmed')
    stopPolling()
  }, 10000)
}

function stopPolling() {
  pollingActive.value = false
  if (pollingInterval) {
    clearTimeout(pollingInterval)
    pollingInterval = null
  }
}

function getStatusLabel(status) {
  const labels = {
    paye: 'Payé',
    failed: 'Échec',
    pending: 'En attente'
  }
  return labels[status] || status
}



</script>

<script>
export default {
  name: 'ProductPurchasePaymentModal'
}
</script>
